import { useEffect, useRef, useState } from 'react';
import type { BylawChat, BylawDocument, BylawMessage } from '@shared/types';
import { Banner, Field, Markdown, Spinner, Tag } from '../components/ui';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { fileSize, relativeTime } from '../lib/format';
import { useToast } from '../lib/toast';
import { useAsync } from '../lib/useAsync';
import { PageHeader } from '../components/PageHeader';

const SUGGESTIONS = [
  'What do the bylaws say about board meetings?',
  'Can a homeowner change the exterior of their house?',
  'What happens if assessments are not paid?',
  'How are the bylaws amended?',
];

const ACCEPT = '.pdf,.docx,.txt,.md,.csv,.png,.jpg,.jpeg,.webp,.gif';

export function BylawsPage() {
  const { isBoard, isSuperAdmin, user } = useAuth();
  const { notify, fail } = useToast();
  const canManageSources = isBoard || isSuperAdmin;
  const folder = useAsync(async () => api.bylaws(), [user?.communityId]);
  const chats = useAsync(async () => api.bylawChats(), [user?.communityId]);

  const [activeId, setActiveId] = useState<string | null>(null);
  const [drafting, setDrafting] = useState(false);
  const [messages, setMessages] = useState<BylawMessage[]>([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const threadFor = useRef<string | null>(null);

  const documents = folder.data?.documents ?? [];
  const readyCount = documents.filter((doc) => doc.status === 'ready').length;
  const aiReady = folder.data?.aiEngine === 'gemini';
  const chatList = chats.data?.chats ?? [];

  useEffect(() => {
    if (drafting || activeId || !chatList.length) return;
    setActiveId(chatList[0].id);
  }, [chatList, activeId, drafting]);

  useEffect(() => {
    if (!activeId) {
      setMessages([]);
      setLoadingThread(false);
      return;
    }
    if (threadFor.current === activeId) return;
    setChatError(null);
    let cancelled = false;
    setLoadingThread(true);
    api
      .bylawChat(activeId)
      .then((result) => {
        if (cancelled) return;
        threadFor.current = activeId;
        setMessages(result.messages);
      })
      .catch((error) => {
        if (!cancelled) fail(error, 'Could not open that chat.');
      })
      .finally(() => {
        if (!cancelled) setLoadingThread(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeId, fail]);

  useEffect(() => {
    const node = threadRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [messages, asking]);

  function applyDocuments(next: BylawDocument[]) {
    folder.setData({ documents: next, aiEngine: folder.data?.aiEngine ?? 'none' });
  }

  function applyChats(next: BylawChat[]) {
    chats.setData({ chats: next });
  }

  function startNewChat() {
    threadFor.current = null;
    setDrafting(true);
    setActiveId(null);
    setMessages([]);
    setQuestion('');
    setChatError(null);
  }

  async function ask(text: string) {
    const message = text.trim();
    if (!message || asking) return;
    setAsking(true);
    setChatError(null);
    setQuestion('');
    const optimistic: BylawMessage = {
      id: `pending-${Date.now()}`,
      role: 'user',
      content: message,
      citations: [],
      createdAt: new Date().toISOString(),
    };
    setMessages((current) => [...current, optimistic]);
    try {
      if (activeId) {
        const result = await api.bylawSend(activeId, message);
        setMessages(result.messages);
        chats.setData({
          chats: chatList.map((chat) =>
            chat.id === activeId ? { ...chat, updatedAt: new Date().toISOString() } : chat,
          ),
        });
      } else {
        const result = await api.bylawStartChat(message);
        threadFor.current = result.chatId;
        setMessages(result.messages);
        setDrafting(false);
        setActiveId(result.chatId);
        applyChats(result.chats);
      }
    } catch (error) {
      setMessages((current) => current.filter((item) => item.id !== optimistic.id));
      setQuestion(message);
      // Keep the reason on screen next to the question; a toast alone is easy to miss.
      setChatError(error instanceof ApiError ? error.message : 'Could not answer that. Please try again.');
      fail(error, 'Could not answer that.');
    } finally {
      setAsking(false);
    }
  }

  async function deleteActiveChat() {
    if (!activeId) return;
    const id = activeId;
    try {
      const result = await api.bylawDeleteChat(id);
      threadFor.current = null;
      applyChats(result.chats);
      setActiveId(result.chats[0]?.id ?? null);
      setDrafting(result.chats.length === 0);
      notify('Chat deleted.');
    } catch (error) {
      fail(error, 'Could not delete that chat.');
    }
  }

  if (!user?.communityId) {
    return (
      <div>
        <PageHeader
          title="Bylaws"
          lede="Join a community before asking about its governing documents."
        />
      </div>
    );
  }

  return (
    <div className="bylaws-page">
      <div className="bylaws-page-head">
        <div>
          <PageHeader
            title="Bylaws"
            lede={
              canManageSources
                ? 'Add governing documents on the left, then ask questions in chat. Answers quote the uploaded sources.'
                : 'Ask questions about your community bylaws. Your chats are saved so you can come back later.'
            }
          />
        </div>
      </div>

      {folder.data?.aiEngine === 'none' && (
        <Banner kind="warn">
          {canManageSources ? (
            <>
              No AI provider key is set, so chat is off. Documents can still be stored and opened. A
              Super Admin can add a key in Super Admin → Settings to let members ask questions.
            </>
          ) : (
            'Chat is not turned on yet. Check back once the board has finished setup.'
          )}
        </Banner>
      )}

      <div className={`bylaws-studio${canManageSources ? ' with-sources' : ' with-history'}`}>
        {canManageSources ? (
          <SourcesPanel
            documents={documents}
            loading={folder.loading && !folder.data}
            onChange={applyDocuments}
          />
        ) : (
          <HistoryPanel
            chats={chatList}
            activeId={activeId}
            drafting={drafting}
            loading={chats.loading && !chats.data}
            onNew={startNewChat}
            onSelect={(id) => {
              setDrafting(false);
              setActiveId(id);
            }}
          />
        )}

        <section className="bylaws-chat-panel" aria-label="Bylaws chat">
          <div className="bylaws-chat-head">
            <div>
              <h2>Chat</h2>
              <p className="mut" style={{ margin: 0 }}>
                {readyCount > 0
                  ? `${readyCount} source${readyCount === 1 ? '' : 's'} ready`
                  : 'No readable sources yet'}
              </p>
            </div>
            {canManageSources && (
              <button type="button" className="btn tiny s" onClick={startNewChat}>
                New chat
              </button>
            )}
          </div>

          {canManageSources && chatList.length > 0 && (
            <div className="bylaw-chats">
              {(drafting || !activeId) && (
                <span className="btn tiny">New chat</span>
              )}
              {chatList.map((chat) => (
                <button
                  key={chat.id}
                  type="button"
                  className={`btn tiny ${chat.id === activeId ? '' : 's'}`}
                  onClick={() => {
                    setDrafting(false);
                    setActiveId(chat.id);
                  }}
                >
                  <span>{chat.title}</span>
                </button>
              ))}
            </div>
          )}

          <div className="bylaw-thread" ref={threadRef} aria-live="polite">
            {loadingThread && <Spinner label="Loading chat" />}
            {!loadingThread && messages.length === 0 && (
              <div className="bylaws-empty-chat">
                <p className="mut">
                  {folder.loading && !folder.data
                    ? 'Loading sources…'
                    : readyCount === 0
                      ? canManageSources
                        ? 'Add a readable source on the left, then ask a question here.'
                        : 'The board has not added readable bylaws yet.'
                      : 'Ask a question about the bylaws, or pick a suggestion.'}
                </p>
                {readyCount > 0 && aiReady && (
                  <div className="chip-row">
                    {SUGGESTIONS.map((prompt) => (
                      <button
                        key={prompt}
                        type="button"
                        className="chip"
                        onClick={() => void ask(prompt)}
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {messages.map((message) => (
              <div key={message.id} className={`bylaw-bubble ${message.role}`}>
                {message.role === 'assistant' ? (
                  <Markdown source={message.content} />
                ) : (
                  message.content
                )}
                {message.citations.length > 0 && (
                  <div className="bylaw-cites">
                    {message.citations.map((cite, index) => (
                      <p key={`${cite.documentTitle}-${index}`}>
                        <strong>{cite.documentTitle}</strong>
                        {cite.location ? <span className="mut"> · {cite.location}</span> : null}.{' '}
                        “{cite.excerpt}”
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {asking && (
              <div className="bylaw-bubble assistant">
                <Spinner label="Looking through the bylaws" /> Looking through the bylaws…
              </div>
            )}
          </div>

          {chatError && (
            <div role="alert">
              <Banner kind="error">{chatError}</Banner>
            </div>
          )}

          <form
            className="bylaw-compose"
            onSubmit={(event) => {
              event.preventDefault();
              void ask(question);
            }}
          >
            <div className="bylaw-compose-field">
              <textarea
                aria-label="Question about the bylaws"
                value={question}
                rows={2}
                placeholder={
                  readyCount === 0
                    ? 'Add a bylaws document before asking'
                    : 'Ask a question about the bylaws'
                }
                disabled={asking || !aiReady || readyCount === 0}
                onChange={(event) => setQuestion(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    void ask(question);
                  }
                }}
              />
              <div className="bylaw-compose-meta">
                <span className="mut">
                  {readyCount} source{readyCount === 1 ? '' : 's'}
                </span>
                <button
                  className="btn bylaw-send"
                  type="submit"
                  disabled={asking || !aiReady || readyCount === 0 || !question.trim()}
                  aria-label={asking ? 'Asking' : 'Send question'}
                >
                  {asking ? '…' : '↑'}
                </button>
              </div>
            </div>
          </form>

          <p className="mut bylaws-disclaimer">
            Answers quote the uploaded documents. They are not legal advice.
            {activeId && (
              <button type="button" className="btn ghost tiny" onClick={() => void deleteActiveChat()}>
                Delete this chat
              </button>
            )}
          </p>
        </section>
      </div>
    </div>
  );
}

function HistoryPanel({
  chats,
  activeId,
  drafting,
  loading,
  onNew,
  onSelect,
}: {
  chats: BylawChat[];
  activeId: string | null;
  drafting: boolean;
  loading: boolean;
  onNew: () => void;
  onSelect: (id: string) => void;
}) {
  return (
    <aside className="bylaws-side-panel" aria-label="Your chats">
      <div className="bylaws-side-head">
        <h2>Chats</h2>
      </div>
      <button type="button" className="btn tiny bylaws-add-btn" onClick={onNew}>
        + New chat
      </button>
      {loading && <Spinner label="Loading chats" />}
      {!loading && chats.length === 0 && (
        <p className="mut bylaws-side-empty">Your questions will show up here so you can reopen them later.</p>
      )}
      <ul className="bylaws-side-list">
        {(drafting || !activeId) && chats.length > 0 && (
          <li>
            <div className="bylaws-side-item active">
              <strong>New chat</strong>
              <span className="mut">Draft</span>
            </div>
          </li>
        )}
        {chats.map((chat) => (
          <li key={chat.id}>
            <button
              type="button"
              className={`bylaws-side-item${chat.id === activeId ? ' active' : ''}`}
              onClick={() => onSelect(chat.id)}
            >
              <strong>{chat.title}</strong>
              <span className="mut">{relativeTime(chat.updatedAt)}</span>
            </button>
          </li>
        ))}
      </ul>
    </aside>
  );
}

function SourcesPanel({
  documents,
  loading,
  onChange,
}: {
  documents: BylawDocument[];
  loading: boolean;
  onChange: (documents: BylawDocument[]) => void;
}) {
  const { notify, fail } = useToast();
  const [title, setTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [menuId, setMenuId] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const reading = documents.some((doc) => doc.status === 'processing');

  useEffect(() => {
    if (!reading) return;
    let cancelled = false;
    const tick = window.setInterval(() => {
      void api
        .bylaws()
        .then((result) => {
          if (!cancelled) onChange(result.documents);
        })
        .catch(() => undefined);
    }, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(tick);
    };
  }, [reading]); // onChange intentionally omitted — folder.setData is stable

  async function uploadFiles(fileList: FileList | File[]) {
    const picked = [...fileList].slice(0, 3);
    if (picked.length === 0) return;
    if ([...fileList].length > 3) notify('Only the first 3 files will be added.');
    setUploading(true);
    const previous = new Set(documents.map((doc) => doc.id));
    try {
      const result = await api.bylawUpload(picked, picked.length === 1 ? title : undefined);
      onChange(result.documents);
      setTitle('');
      setAdding(false);
      if (fileRef.current) fileRef.current.value = '';
      const added = result.documents.filter((doc) => !previous.has(doc.id));
      const failed = added.filter((doc) => doc.status === 'failed');
      const pending = added.filter((doc) => doc.status === 'processing');
      if (failed.length > 0) {
        notify(
          failed.length === 1
            ? `Saved “${failed[0].title}”, but it could not be read for chat.`
            : 'Saved the files, but some could not be read for chat.',
          'error',
        );
      } else if (pending.length > 0) {
        notify(
          pending.length === 1
            ? `Saved “${pending[0].title}”. Reading it in the background — large PDFs can take a few minutes.`
            : 'Saved the files. Reading them in the background — large PDFs can take a few minutes.',
        );
      } else {
        notify(picked.length === 1 ? 'Source added.' : 'Sources added.');
      }
    } catch (error) {
      fail(error, 'That upload did not go through.');
    } finally {
      setUploading(false);
    }
  }

  async function rename(id: string) {
    const next = renameValue.trim();
    if (!next) return;
    setBusyId(id);
    try {
      const result = await api.bylawRename(id, next);
      onChange(result.documents);
      setRenaming(null);
      notify('Title updated.');
    } catch (error) {
      fail(error, 'Could not rename that document.');
    } finally {
      setBusyId(null);
    }
  }

  async function reread(id: string) {
    setBusyId(id);
    setMenuId(null);
    try {
      const result = await api.bylawReread(id);
      onChange(result.documents);
      const doc = result.documents.find((item) => item.id === id);
      if (doc?.status === 'ready') notify('That document is ready for chat.', 'success');
      else if (doc?.status === 'processing') {
        notify('Reading that document in the background. Large PDFs can take a few minutes.');
      } else {
        notify('Still could not read that file.', 'error');
      }
    } catch (error) {
      fail(error, 'Could not read that document again.');
    } finally {
      setBusyId(null);
    }
  }

  async function remove(doc: BylawDocument) {
    if (!window.confirm(`Remove “${doc.title}” from sources?`)) return;
    setBusyId(doc.id);
    setMenuId(null);
    try {
      const result = await api.bylawDelete(doc.id);
      onChange(result.documents);
      notify('Source removed.');
    } catch (error) {
      fail(error, 'Could not remove that document.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <aside className="bylaws-side-panel" aria-label="Sources">
      <div className="bylaws-side-head">
        <h2>Sources</h2>
        <Tag>{documents.length}</Tag>
      </div>

      <button
        type="button"
        className="btn tiny bylaws-add-btn"
        disabled={uploading}
        onClick={() => setAdding((open) => !open)}
      >
        + Sources
      </button>

      {adding && (
        <form
          className="bylaws-add-form"
          onSubmit={(event) => {
            event.preventDefault();
            const files = fileRef.current?.files;
            if (files?.length) void uploadFiles(files);
          }}
        >
          <Field label="Title" hint="Optional when adding one file.">
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Master covenant"
            />
          </Field>
          <Field label="Files">
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              multiple
              onChange={(event) => {
                if (event.target.files?.length) void uploadFiles(event.target.files);
              }}
            />
          </Field>
          <p className="mut" style={{ margin: 0 }}>
            PDF, Word, text, or image · up to 6 MB each
          </p>
          {uploading && <Spinner label="Uploading" />}
        </form>
      )}

      {loading && <Spinner label="Loading sources" />}
      {!loading && documents.length === 0 && (
        <p className="mut bylaws-side-empty">
          Add the governing documents members should be able to ask about.
        </p>
      )}

      <ul className="bylaws-side-list">
        {documents.map((doc) => (
          <li key={doc.id} className="bylaws-source-item">
            {renaming === doc.id ? (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  void rename(doc.id);
                }}
              >
                <Field label="Title">
                  <input
                    value={renameValue}
                    onChange={(event) => setRenameValue(event.target.value)}
                    autoFocus
                  />
                </Field>
                <div className="btn-row">
                  <button className="btn tiny" type="submit" disabled={busyId === doc.id}>
                    Save
                  </button>
                  <button className="btn tiny s" type="button" onClick={() => setRenaming(null)}>
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className="bylaws-source-row">
                  <span className="bylaws-file-icon" aria-hidden="true">
                    {fileExt(doc.filename)}
                  </span>
                  <div className="bylaws-source-copy">
                    <strong title={doc.title}>{doc.title}</strong>
                    <span className="mut">
                      {doc.filename} · {fileSize(doc.size)}
                    </span>
                    {doc.error && <span className="warn">{doc.error}</span>}
                  </div>
                  <Status status={doc.status} />
                </div>
                <div className="bylaws-source-actions">
                  <a className="btn tiny ghost" href={doc.url} target="_blank" rel="noreferrer">
                    Open
                  </a>
                  <button
                    type="button"
                    className="btn tiny ghost"
                    aria-expanded={menuId === doc.id}
                    onClick={() => setMenuId((current) => (current === doc.id ? null : doc.id))}
                  >
                    More
                  </button>
                </div>
                {menuId === doc.id && (
                  <div className="bylaws-source-menu">
                    <button
                      type="button"
                      onClick={() => {
                        setRenaming(doc.id);
                        setRenameValue(doc.title);
                        setMenuId(null);
                      }}
                    >
                      Rename
                    </button>
                    {doc.status !== 'ready' && (
                      <button
                        type="button"
                        disabled={busyId === doc.id}
                        onClick={() => void reread(doc.id)}
                      >
                        {busyId === doc.id ? 'Reading…' : 'Read again'}
                      </button>
                    )}
                    <button
                      type="button"
                      className="danger"
                      disabled={busyId === doc.id}
                      onClick={() => void remove(doc)}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </>
            )}
          </li>
        ))}
      </ul>
    </aside>
  );
}

function Status({ status }: { status: BylawDocument['status'] }) {
  if (status === 'ready') return <Tag tone="ok">Ready</Tag>;
  if (status === 'failed') return <Tag tone="bad">Not readable</Tag>;
  return <Tag tone="pend">Reading</Tag>;
}

function fileExt(filename: string) {
  const ext = filename.split('.').pop()?.toUpperCase() ?? 'FILE';
  return ext.slice(0, 4) || 'FILE';
}
